import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, SafeAreaView, ScrollView, TouchableOpacity, TextInput, Alert, Platform, ActivityIndicator, Switch } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useThemeColors } from '../constants/theme';
import { useRouter } from 'expo-router';
import { Colors, SIZES } from '../constants/theme';
import { useAppStore } from '../store/useAppStore';
import { firestoreService } from '../services/firestoreService';
import { callAprobarCandidato, callRechazarCandidato } from '../services/cloudFunctions';
import { leerVehiculo } from '../constants/vehiculoDeFlota';
import { mensajeDeError } from '../utils/mensajeDeError';
import { authBridge } from '../services/authBridge';
import { RemoteConfigService } from '../services/remoteConfig';
// TIEMPO MÁXIMO — el CRM vive en Railway, que fue BORRADO. Un `fetch` sin
// AbortController contra un host muerto puede no volver nunca y deja la
// pantalla del dueño colgada; `fetchWithRetry` corta cada intento y no
// reintenta los 4xx.
import { fetchWithRetry } from '../services/fetchWithRetry';

/** 2 intentos de 10 s: el dueño espera ~20 s en el peor caso, no infinito. */
const RED_CRM = { retries: 2, perAttemptTimeoutMs: 10_000 } as const;

const CRM_BACKEND = 'https://go-safely-backend-production.up.railway.app';

/**
 * Dónde vive la web de Go Safely. Es la base del enlace mágico del rent car.
 *
 * ⚠️ El enlace tiene que abrirse SIN APP y SIN SESIÓN: el candidato es un
 * desconocido con un WhatsApp. Por eso `/onboard/**` ya no está en la lista de
 * redirecciones de `firebase.json` ni fuera de `PUBLIC_WEB_ROUTES`
 * (src/app/_layout.tsx) — estaba cerrado por los dos sitios y aterrizaba en el
 * login de los dueños, que además echa a quien no lo es.
 */
const DOMINIO_PUBLICO = 'https://gosafely-3e0e7.web.app';

/**
 * Interruptor de la pestaña API/Bot del CRM (configuración de WhatsApp + IA).
 *
 * En false porque TODO lo que hay detrás — cargar la config, guardarla y la
 * URL del webhook que se le muestra al dueño — vive en el backend de Railway,
 * que fue BORRADO y responde 404 en todas sus rutas.
 *
 * Ponlo en true el día que ese CRM viva en Cloud Functions. El código de la
 * pestaña sigue completo en este archivo: no hay que reescribir nada, solo
 * volver a agregar 'CONFIG' al array de pestañas de abajo.
 */
const CRM_CONFIG_HABILITADO = false;
// El DesktopSidebarLayout ahora se aplica desde el root layout (src/app/_layout.tsx)
// para que el sidebar persista en TODAS las rutas del owner. Antes este archivo
// envolvía su contenido aparte y eso causaba doble sidebar en web.
import * as Clipboard from 'expo-clipboard';
import { volver } from '../utils/volver';
import { pesos } from '../utils/dinero';

/**
 * Fecha en que el candidato mandó sus papeles. El campo real es `submittedAt`
 * (functions/src/drivers.ts) y llega como Timestamp de Firestore; los
 * documentos viejos pueden traer `joinedAt` o nada. Si no hay forma de
 * resolverla se DICE "sin dato" — no se inventa un "hoy".
 */
function fechaRecibido(cand: any): string {
  const bruto = cand?.submittedAt ?? cand?.joinedAt ?? cand?.date;
  if (!bruto) return 'sin dato';
  try {
    const d =
      typeof bruto?.toDate === 'function' ? bruto.toDate()
      : bruto instanceof Date ? bruto
      : typeof bruto === 'number' ? new Date(bruto)
      : typeof bruto?.seconds === 'number' ? new Date(bruto.seconds * 1000)
      : new Date(String(bruto));
    if (isNaN(d.getTime())) return 'sin dato';
    return d.toLocaleDateString('es-DO', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return 'sin dato';
  }
}

/** `PENDING_REVIEW` es el nombre del campo en la base, no algo que leerle al dueño. */
function estadoLegible(status: any): string {
  if (status === 'PENDING_REVIEW') return 'Por revisar';
  if (status === 'Aprobado') return 'Aprobado';
  return typeof status === 'string' && status.trim() ? status : 'Por revisar';
}

export default function CRMSettingsScreen() {
  const colors = useThemeColors();
  const router = useRouter();
  const theme = useAppStore((state) => state.theme);
  const isDark = theme === 'dark';

  // Config de APIs
  const [whatsappKey, setWhatsappKey] = useState('');
  const [whatsappPhone, setWhatsappPhone] = useState('');
  const [whatsappPhoneNumberId, setWhatsappPhoneNumberId] = useState('');
  const [whatsappVerifyToken, setWhatsappVerifyToken] = useState('');
  const [webhookUrl, setWebhookUrl] = useState('https://go-safely-backend-production.up.railway.app/api/crm/whatsapp/webhook');
  const [botPrompt, setBotPrompt] = useState('Eres el asistente virtual inteligente de Rent Car VIP. Tu rol es dar soporte en tiempo real a nuestros conductores, procesar reportes de mantenimiento, y recordar cordialmente el pago de la renta del auto los lunes por la mañana.');
  const [aiProvider, setAiProvider] = useState<'GEMINI' | 'OPENAI' | 'ANTHROPIC'>('GEMINI');
  const [aiApiKey, setAiApiKey] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Automatizaciones
  const [botEnabled, setBotEnabled] = useState(true);
  const [paymentAlerts, setPaymentAlerts] = useState(true);
  const [tripAlerts, setTripAlerts] = useState(true);

  // Datos
  const candidates = useAppStore((state) => state.rentCarCandidates);
  const trips = useAppStore((state) => state.rentCarTrips);
  const vehicles = useAppStore((state) => state.rentCarVehicles);
  const drivers = useAppStore((state) => state.rentCarDrivers);
  const rejectCandidateDriver = useAppStore((state) => state.rejectCandidateDriver);
  const userFirebaseUid = useAppStore((state) => state.userFirebaseUid);
  const setRentCarCandidates = useAppStore((state) => state.setRentCarCandidates);

  // FIX CRM — Cargar en vivo los candidatos/choferes del CRM del dueño desde
  // Firestore (/users/{uid}/crm_drivers). Antes rentCarCandidates SIEMPRE
  // estaba vacío porque nadie llamaba al listener → el panel CRM salía sin
  // datos aunque hubiera candidatos onboarded.
  useEffect(() => {
    if (!userFirebaseUid) return;
    const unsub = firestoreService.listenToCRMDrivers(userFirebaseUid, (list: any[]) => {
      setRentCarCandidates(list);
    });
    return () => { if (typeof unsub === 'function') unsub(); };
  }, [userFirebaseUid, setRentCarCandidates]);

  // `candidates` y `drivers` salen del MISMO listener (users/{uid}/crm_drivers)
  // sin filtrar, así que la lista trae mezclados los candidatos por revisar
  // (`status: 'PENDING_REVIEW'`, los escribe submitCandidateOnboarding) y los
  // choferes ya aprobados (`status: 'Aprobado'`, los escribe
  // onboardDriverToCRM en un documento NUEVO). Se separan aquí una sola vez.
  //
  // Al desconocido se le trata como PENDIENTE a propósito: si algún día llega
  // un documento con otro `status`, es preferible que el dueño lo vea y decida
  // a que desaparezca de las dos listas sin que nadie se entere.
  const choferesActivos = React.useMemo(
    () => drivers.filter((d: any) => d?.status === 'Aprobado'),
    [drivers]
  );
  const solicitudesPendientes = React.useMemo(
    () => candidates.filter((c: any) => c?.status !== 'Aprobado'),
    [candidates]
  );

  const [activeTab, setActiveTab] = useState<'PANEL' | 'CONFIG' | 'CHOFERES' | 'VIAJES'>('PANEL');
  const [candidateToAssign, setCandidateToAssign] = useState<{id: string, name: string} | null>(null);

  // FIX CRM — Cargar la config guardada (BYOK) desde el backend. Las claves
  // llegan ENMASCARADAS (nunca en claro); si están puestas, mostramos ••••.
  useEffect(() => {
    if (!userFirebaseUid) return;
    // DESACTIVADO junto con la pestaña API/Bot. Este cargador corría al ABRIR
    // la pantalla del CRM, aunque el dueño solo quisiera ver sus reportes:
    // esperaba a un backend borrado hasta agotar el timeout de red en cada
    // entrada. Sin él, Reportes/Viajes/Candidatos abren al instante.
    // Reactivar junto con la pestaña cuando el CRM viva en Cloud Functions.
    if (!CRM_CONFIG_HABILITADO) return;
    (async () => {
      try {
        const token = await authBridge.getIdToken();
        if (!token) return;
        const resp = await fetchWithRetry(`${CRM_BACKEND}/api/crm/config`, {
          headers: { Authorization: `Bearer ${token}` },
          ...RED_CRM,
        });
        if (!resp.ok) return;
        const c = await resp.json();
        setWhatsappPhone(c.whatsappPhone || '');
        setWhatsappPhoneNumberId(c.whatsappPhoneNumberId || '');
        setWhatsappVerifyToken(c.whatsappVerifyToken || '');
        if (c.webhookUrl) setWebhookUrl(c.webhookUrl);
        setAiProvider((['GEMINI', 'OPENAI', 'ANTHROPIC'].includes(c.aiProvider) ? c.aiProvider : 'GEMINI'));
        if (c.botPrompt) setBotPrompt(c.botPrompt);
        setBotEnabled(c.botEnabled !== false);
        setPaymentAlerts(c.paymentAlerts !== false);
        setTripAlerts(c.tripAlerts !== false);
        if (c.hasWhatsappKey) setWhatsappKey('••••••••');
        if (c.hasAiKey) setAiApiKey('••••••••');
      } catch { /* sin conexión: se queda con los defaults */ }
    })();
  }, [userFirebaseUid]);

  // FIX CRM — Guardado REAL y SEGURO (BYOK). Antes era un stub (setTimeout +
  // Alert) que no persistía nada. Ahora envía al backend, que CIFRA las claves
  // (WhatsApp + IA) antes de guardarlas. Las claves nunca vuelven en claro.
  const handleSaveConfig = async () => {
    if (!userFirebaseUid) {
      Alert.alert('Sesión requerida', 'Inicia sesión como dueño Rent Car para guardar tu configuración.');
      return;
    }
    setIsSaving(true);
    try {
      const token = await authBridge.getIdToken();
      if (!token) {
        Alert.alert('Sesión requerida', 'Vuelve a iniciar sesión e intenta de nuevo.');
        return;
      }
      const resp = await fetchWithRetry(`${CRM_BACKEND}/api/crm/save-config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ whatsappPhone, whatsappPhoneNumberId, whatsappVerifyToken, whatsappKey, aiProvider, aiApiKey, botPrompt, botEnabled, paymentAlerts, tripAlerts }),
        ...RED_CRM,
      });
      const data = await resp.json().catch(() => null);
      if (!resp.ok || !data?.ok) {
        Alert.alert('Error', (data && data.error) || 'No se pudo guardar la configuración.');
        return;
      }
      // Re-enmascarar las claves que quedaron guardadas.
      if (data.hasWhatsappKey) setWhatsappKey('••••••••');
      if (data.hasAiKey) setAiApiKey('••••••••');
      Alert.alert('Guardado', 'Tu configuración se guardó de forma segura. Tus claves quedan cifradas y solo las usa el bot; nunca se muestran.');
    } catch {
      Alert.alert('Error', 'Sin conexión estable. Intenta de nuevo.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      // FIX CRM — Antes el enlace usaba "FLOTA123" hardcodeado, así que el
      // candidato quedaba onboarded bajo un dueño FALSO (/users/FLOTA123/...)
      // y NUNCA aparecía en el CRM del dueño real. Ahora usa su uid real.
      const userId = userFirebaseUid;
      if (!userId) {
        Alert.alert('Sesión requerida', 'Inicia sesión como dueño Rent Car para generar tu enlace.');
        return;
      }
      // ══════════════════════════════════════════════════════════════════
      // ESTE BOTÓN COPIABA LA FICHA DE LA PLAY STORE, NO EL ENLACE MÁGICO
      // ══════════════════════════════════════════════════════════════════
      //
      // Decía «Copiar Enlace Mágico» y llamaba a
      // `RemoteConfigService.generateShareMessage(userId, 'RENT_CAR_OWNER')`,
      // que devuelve el mensaje de INVITACIÓN A LA APP: un pitch más la
      // dirección de Google Play. El `userId` que se le pasaba ni se usaba para
      // el enlace.
      //
      // O sea: el dueño tocaba el botón, pegaba lo copiado en WhatsApp y se lo
      // mandaba al candidato. El candidato recibía «descárgate Go Safely» con
      // un enlace a la tienda — sin `/onboard/`, sin ningún código que dijera
      // de QUÉ dueño venía. Se instalaba la app, se registraba por su cuenta…
      // y nunca aparecía en el panel de nadie. El dueño lo esperaba y el
      // candidato creía haberlo hecho bien.
      //
      // El enlace de verdad es `/onboard/{uid del dueño}`, que es lo que lee
      // `src/app/onboard/[id].tsx` para saber a quién vincular al candidato.
      const enlace = `${DOMINIO_PUBLICO}/onboard/${userId}`;
      const message =
        `Hola, soy de Go Safely. Para empezar a trabajar con uno de mis vehículos, ` +
        `completa tu registro aquí:\n\n${enlace}\n\n` +
        `Se abre en el navegador, no hace falta que instales nada. Cuando termines ` +
        `me aparece tu solicitud y te contesto.`;
      await Clipboard.setStringAsync(message);
      if (Platform.OS === 'web') {
        window.alert('¡Copiado! Pégalo en WhatsApp y mándaselo a tu candidato.');
      } else {
        Alert.alert('¡Copiado!', 'Pégalo en WhatsApp y mándaselo a tu candidato.');
      }
    } catch (error) {
      if (Platform.OS === 'web') window.alert("Error: No se pudo copiar.");
      else Alert.alert("Error", "No se pudo generar el enlace dinámico.");
    }
  };

  const openAssignModal = (id: string, name: string) => {
    setCandidateToAssign({ id, name });
  };

  // ── LO QUE ESTOS DOS BOTONES HACEN DE VERDAD ───────────────────────────
  //
  // ── LO QUE ESTOS DOS BOTONES HACEN AHORA ────────────────────────────────
  //
  // Hasta el 3-oct-2026 los dos mentian. APROBAR vivia entero en el telefono:
  // el `vehicleId` que este modal recoge NO SE USABA EN NINGUNA PARTE, el carro
  // no se marcaba como entregado, la ficha escribia `vehicle: 'Pendiente'`
  // pisando el vehiculo elegido, y al candidato no le llegaba nada. RECHAZAR
  // solo lo quitaba de la lista EN MEMORIA: volvia a aparecer al recargar.
  //
  // Ahora los dos pasan por el servidor (`aprobarCandidatoDeFlota` y
  // `rechazarCandidatoDeFlota`, functions/src/drivers.ts), que es donde se
  // puede: tocan dos documentos de dos duenos distintos a la vez.
  //
  const [procesandoCandidato, setProcesandoCandidato] = useState(false);

  const avisar = (titulo: string, cuerpo: string) => {
    // `Alert.alert` de react-native-web no pinta NADA, y este panel se usa
    // mucho desde el navegador: sin esto serian botones mudos.
    if (Platform.OS === 'web') window.alert(titulo + String.fromCharCode(10, 10) + cuerpo);
    else Alert.alert(titulo, cuerpo);
  };

  const handleApproveCandidate = async (vehicleId: string) => {
    if (!candidateToAssign || procesandoCandidato) return;
    setProcesandoCandidato(true);
    try {
      const r = await callAprobarCandidato({ candidateUid: candidateToAssign.id, vehicleId });
      setCandidateToAssign(null);
      avisar(
        'Chofer aprobado',
        `${candidateToAssign.name} ya tiene asignado el ${r.data?.vehiculo ?? 'vehículo'}. ` +
          `El carro deja de aparecer publicado en el Marketplace y a él le llegó el aviso. ` +
          `Cuando te lo devuelva, suéltalo desde tu Flota para volver a ofrecerlo.`
      );
    } catch (e: any) {
      // El modal NO se cierra: si falló, cerrarlo haría creer que se aprobó.
      avisar('No se pudo aprobar', mensajeDeError(e, 'Revisa tu conexión e inténtalo de nuevo.'));
    } finally {
      setProcesandoCandidato(false);
    }
  };

  const handleRejectCandidate = async (id: string, name: string) => {
    if (procesandoCandidato) return;
    setProcesandoCandidato(true);
    try {
      await callRechazarCandidato({ candidateUid: id });
      // La lista local se limpia solo cuando el servidor lo confirmó. Antes se
      // quitaba de memoria SIN guardar nada: la solicitud volvía a aparecer al
      // recargar y al candidato no le llegaba nada.
      rejectCandidateDriver(id);
      avisar(
        'Solicitud rechazada',
        `${name} ya no aparece en tu lista y le avisamos. Su solicitud queda guardada como rechazada, ` +
          `así que no te vuelve a salir.`
      );
    } catch (e: any) {
      avisar('No se pudo rechazar', mensajeDeError(e, 'Revisa tu conexión e inténtalo de nuevo.'));
    } finally {
      setProcesandoCandidato(false);
    }
  };

  return (
    <SafeAreaView style={[styles.safeArea, { backgroundColor: colors.background }]}>
      <View style={[styles.header, { borderBottomColor: colors.border }]}>
        <TouchableOpacity onPress={() => Platform.OS === 'web' ? router.replace('/(tabs)/dashboard') : volver(router)} style={styles.backButton}>
          <Ionicons name={Platform.OS === 'web' ? "close" : "arrow-back"} size={28} color={colors.text} />
        </TouchableOpacity>
        <Text style={[styles.headerTitle, { color: colors.text }]}>CRM Inteligente Rent Car</Text>
        <View style={{ width: 40 }} />
      </View>

      {/* TABS DE NAVEGACION CRM
          'CONFIG' (API/Bot) queda FUERA de esta release: todo su contenido
          — cargar y guardar la configuración, y la URL del webhook de
          WhatsApp que se le muestra al dueño para pegarla en Meta — apunta
          al backend de Railway, que fue borrado. Entrar ahí significaba ver
          el formulario vacío (parecía que se te borró la configuración) y
          recibir "No se pudo guardar" al intentar arreglarlo. La URL que
          copiaba, además, era de un host muerto.
          Se reactiva agregando 'CONFIG' de vuelta a este array cuando el
          CRM viva en Cloud Functions. El bloque de la pestaña sigue en el
          archivo, intacto — no hay que reescribirlo.

          'VIAJES' sale por lo mismo, y es peor porque no se notaba: la pestaña
          abría, pintaba el encabezado "MONITOREO DE VIAJES EN TIEMPO REAL" y
          recorría `rentCarTrips`, que NADIE llena nunca. En todo el proyecto no
          existe un `setRentCarTrips` (useAppStore.ts solo lo declara en `[]` y
          lo vuelve a poner en `[]` al cerrar sesión), así que el dueño veía un
          título de monitoreo en vivo sobre una pantalla en blanco, sin ni
          siquiera un "no hay viajes". Es el patrón exacto por el que ya nos
          rechazaron ("la app no carga"). Se reactiva agregando 'VIAJES' de
          vuelta a este array el día que exista quien escriba esa lista: hace
          falta un listener de los viajes de la flota (los viajes viven en
          /rides, y hoy ninguna consulta los cruza con los choferes del CRM del
          dueño). El bloque de la pestaña queda abajo, intacto. */}
      <View style={[styles.tabBar, { borderBottomColor: colors.border }]}>
        {(['PANEL', 'CHOFERES'] as const).map((tab) => (
          <TouchableOpacity
            key={tab}
            style={[styles.tabButton, activeTab === tab && { borderBottomColor: colors.primary }]}
            onPress={() => setActiveTab(tab)}
          >
            <Text style={[styles.tabText, { color: activeTab === tab ? colors.primary : colors.textSecondary }]}>
              {/* Las etiquetas de 'VIAJES' y 'CONFIG' salieron con sus pestañas:
                  el array de arriba ya no las contiene y TypeScript marcaba la
                  comparación como imposible. Al devolverlas al array hay que
                  devolver también su rama aquí ('Viajes' / 'API/Bot'). */}
              {tab === 'PANEL' ? 'Reportes' : 'Candidatos'}
            </Text>
          </TouchableOpacity>
        ))}
      </View>

      <ScrollView contentContainerStyle={styles.container} showsVerticalScrollIndicator={false}>

        {/* TAB 1: DASHBOARD / REPORTES */}
        {activeTab === 'PANEL' && (
          <View>
            <Text style={[styles.sectionTitle, { color: colors.textSecondary }]}>ESTADÍSTICAS AUTOMATIZADAS</Text>
            
            {/* Tarjetas KPI */}
            <View style={styles.kpiContainer}>
              <View style={[styles.kpiCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
                <Ionicons name="cash" size={24} color="#4CAF50" />
                <Text style={[styles.kpiValue, { color: colors.text }]}>
                  {pesos(vehicles.filter(v => v.status === 'Rentado').reduce((sum, v) => sum + v.price, 0))}
                </Text>
                <Text style={[styles.kpiLabel, { color: colors.textSecondary }]}>Ingresos Semanales</Text>
              </View>

              <View style={[styles.kpiCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
                <Ionicons name="car-sport" size={24} color={colors.primary} />
                <Text style={[styles.kpiValue, { color: colors.text }]}>
                  {vehicles.filter(v => v.status === 'Rentado').length} / {vehicles.length}
                </Text>
                <Text style={[styles.kpiLabel, { color: colors.textSecondary }]}>Vehículos Rentados</Text>
              </View>
            </View>

            {/* EL NÚMERO QUE ESTABA AQUÍ NO CONTABA NADA.
                Decía "Mensajes Automatizados" y valía
                `drivers.length * 45 + trips.length * 12`: una fórmula
                inventada, sin ninguna relación con un mensaje enviado. Y no
                podía tenerla — el bot de WhatsApp vivía en el backend de
                Railway, que fue BORRADO y responde 404 en todas sus rutas
                (por eso `CRM_CONFIG_HABILITADO` está en false arriba). Nunca
                se envió un mensaje, así que el contador siempre fue ficción, y
                además crecía solo con `trips`, que es una lista que nadie
                llena.
                En su lugar va un dato REAL, que sale de los documentos que el
                dueño ya tiene en `users/{uid}/crm_drivers`. */}
            <View style={styles.kpiContainer}>
              <View style={[styles.kpiCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
                <Ionicons name="document-text" size={24} color="#FF9500" />
                <Text style={[styles.kpiValue, { color: colors.text }]}>{solicitudesPendientes.length}</Text>
                <Text style={[styles.kpiLabel, { color: colors.textSecondary }]}>Solicitudes por revisar</Text>
              </View>

              {/* "Choferes Activos" contaba TODOS los documentos de crm_drivers,
                  que incluyen a los candidatos que aún no has revisado (llegan
                  con `status: 'PENDING_REVIEW'` desde submitCandidateOnboarding)
                  y, encima, duplican: aprobar crea un documento NUEVO con
                  `status: 'Aprobado'` sin tocar el del candidato, así que cada
                  chofer aprobado sumaba dos. Ahora cuenta lo que dice la
                  etiqueta. */}
              <View style={[styles.kpiCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
                <Ionicons name="people" size={24} color="#9C27B0" />
                <Text style={[styles.kpiValue, { color: colors.text }]}>{choferesActivos.length}</Text>
                <Text style={[styles.kpiLabel, { color: colors.textSecondary }]}>Choferes Activos</Text>
              </View>
            </View>

            {/* AQUÍ HABÍA UN INTERRUPTOR QUE NO APAGABA NADA.
                "Estado del Bot de WhatsApp" estaba cableado a `botEnabled`, un
                `useState` local: al moverlo no se guardaba en ningún sitio.
                Su único guardado estaba en la pestaña API/Bot, que está
                desactivada (CRM_CONFIG_HABILITADO = false) y que además
                escribía en el backend de Railway, borrado. Cerrar la pantalla
                lo devolvía a su valor inicial, y el dueño se quedaba creyendo
                que había encendido o apagado un bot que no existe.
                Un interruptor que no guarda no es un interruptor: se quita y
                se dice la verdad. Vuelve —con su Switch— el día que el bot
                viva en Cloud Functions y haya dónde persistir el valor. */}
            <View style={[styles.card, { backgroundColor: colors.card, borderColor: colors.border, marginTop: 20 }]}>
              <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 6 }}>
                <Ionicons name="logo-whatsapp" size={20} color={colors.textSecondary} style={{ marginRight: 8 }} />
                <Text style={[styles.cardTitle, { color: colors.text }]}>Bot de WhatsApp — próximamente</Text>
              </View>
              <Text style={[styles.cardDescription, { color: colors.textSecondary }]}>
                Todavía no está disponible: no hay ningún mensaje automático saliendo a tus choferes, ni
                recordatorios de renta ni avisos de viaje. Te avisaremos cuando puedas conectarlo. Mientras
                tanto, escríbeles tú por el chat de la app.
              </Text>
            </View>
          </View>
        )}

        {/* TAB 2: SEGUIMIENTO DE VIAJES */}
        {activeTab === 'VIAJES' && (
          <View>
            <Text style={[styles.sectionTitle, { color: colors.textSecondary }]}>MONITOREO DE VIAJES EN TIEMPO REAL</Text>
            {trips.map((trip) => (
              <View key={trip.id} style={[styles.card, { backgroundColor: colors.card, borderColor: colors.border }]}>
                <View style={styles.rowBetween}>
                  <View>
                    <Text style={[styles.driverName, { color: colors.text }]}>{trip.driver}</Text>
                    <Text style={[styles.cardDescription, { color: colors.textSecondary }]}>{trip.car}</Text>
                  </View>
                  <View style={[styles.statusBadge, { backgroundColor: trip.status === 'En Ruta' ? 'rgba(33, 150, 243, 0.1)' : 'rgba(76, 175, 80, 0.1)' }]}>
                    <Text style={[styles.statusText, { color: trip.status === 'En Ruta' ? '#2196F3' : '#4CAF50' }]}>{trip.status}</Text>
                  </View>
                </View>
                
                <View style={[styles.divider, { backgroundColor: colors.border, marginVertical: 12 }]} />
                
                <View style={styles.rowBetween}>
                  <Text style={{ color: colors.textSecondary, fontSize: 13 }}>Destino: <Text style={{ color: colors.text, fontWeight: '600' }}>{trip.destination}</Text></Text>
                  <Text style={{ color: colors.primary, fontWeight: 'bold' }}>+{pesos(trip.earnings)}</Text>
                </View>
              </View>
            ))}
          </View>
        )}

        {/* TAB 3: CANDIDATOS VINCULADOS CON EL ENLACE MÁGICO */}
        {activeTab === 'CHOFERES' && (
          <View>
            <Text style={[styles.sectionTitle, { color: colors.textSecondary }]}>CANDIDATOS (ENLACE MÁGICO)</Text>
            <View style={[styles.card, { backgroundColor: colors.card, borderColor: colors.border }]}>
              <Text style={[styles.cardDescription, { color: colors.textSecondary, marginBottom: 15 }]}>
                Los choferes que completen el registro con tu enlace mágico aparecerán aquí. Los sellos te dicen qué subió cada uno: verde si lo mandó, rojo si falta. Esta pantalla no te abre las fotos y ningún sistema las verifica solo, así que pídeselas al chofer si quieres verlas antes de entregarle un vehículo.
              </Text>
              <TouchableOpacity style={[styles.copyBtn, { borderColor: colors.primary }]} onPress={handleCopyLink}>
                <Ionicons name="copy-outline" size={20} color={colors.primary} />
                <Text style={[styles.copyBtnText, { color: colors.primary }]}>Copiar Enlace Mágico</Text>
              </TouchableOpacity>
            </View>

            <Text style={[styles.sectionTitle, { color: colors.textSecondary, marginTop: 15 }]}>SOLICITUDES PENDIENTES</Text>
            {/* ESTE BLOQUE TUMBABA LA PANTALLA CON EL PRIMER CANDIDATO REAL.
                Leía `cand.docs.id` / `.license` / `.conduct`, y el documento que
                escribe `submitCandidateOnboarding` (functions/src/drivers.ts:64)
                NO tiene ningún objeto `docs`: tiene `idDocUrl`, `licenseUrl` y
                `conductUrl` sueltos. Con la lista vacía no se veía; en cuanto
                entraba un candidato, `cand.docs` era `undefined` y leerle `.id`
                reventaba el render de toda la pestaña. Como la lista está
                tipada `any[]`, `tsc` pasaba en verde.
                Y `cand.date` tampoco existe (el campo es `submittedAt`), así
                que la ficha decía "Recibido:" y nada detrás. */}
            {solicitudesPendientes.length === 0 ? (
              <Text style={{ color: colors.textSecondary, textAlign: 'center', marginVertical: 20 }}>No hay solicitudes pendientes.</Text>
            ) : (
              solicitudesPendientes.map((cand: any) => (
                <View key={cand.id} style={[styles.card, { backgroundColor: colors.card, borderColor: colors.border }]}>
                  <View style={styles.rowBetween}>
                    <View>
                      <Text style={[styles.driverName, { color: colors.text }]}>{cand.name || 'Candidato'}</Text>
                      <Text style={{ color: colors.textSecondary, fontSize: 12 }}>Recibido: {fechaRecibido(cand)}</Text>
                    </View>
                    <View style={styles.statusBadge}>
                      <Text style={styles.statusText}>{estadoLegible(cand?.status)}</Text>
                    </View>
                  </View>

                  <View style={{ flexDirection: 'row', gap: 10, marginVertical: 12 }}>
                    <View style={[styles.docBadge, { backgroundColor: cand?.idDocUrl ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)' }]}>
                      <Text style={{ color: cand?.idDocUrl ? '#4CAF50' : '#F44336', fontSize: 11 }}>Cédula</Text>
                    </View>
                    <View style={[styles.docBadge, { backgroundColor: cand?.licenseUrl ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)' }]}>
                      <Text style={{ color: cand?.licenseUrl ? '#4CAF50' : '#F44336', fontSize: 11 }}>Licencia</Text>
                    </View>
                    <View style={[styles.docBadge, { backgroundColor: cand?.conductUrl ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)' }]}>
                      <Text style={{ color: cand?.conductUrl ? '#4CAF50' : '#F44336', fontSize: 11 }}>Conducta</Text>
                    </View>
                  </View>

                  <View style={{ flexDirection: 'row', gap: 10 }}>
                    <TouchableOpacity 
                      style={[styles.actionBtn, { backgroundColor: colors.primary, flex: 1 }]}
                      onPress={() => openAssignModal(cand.id, cand.name)}
                    >
                      <Text style={styles.actionBtnText}>Aprobar y Asignar Vehículo</Text>
                    </TouchableOpacity>
                    <TouchableOpacity 
                      style={[styles.actionBtn, { backgroundColor: 'rgba(244, 67, 54, 0.1)', borderWidth: 1, borderColor: '#F44336' }]}
                      onPress={() => handleRejectCandidate(cand.id, cand.name)}
                    >
                      <Text style={{ color: '#F44336', fontWeight: 'bold' }}>Rechazar</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              ))
            )}
          </View>
        )}

        {/* TAB 4: CONFIGURACION DE APIS & WHATSAPP */}
        {activeTab === 'CONFIG' && (
          <View>
             <Text style={[styles.sectionTitle, { color: colors.textSecondary }]}>INTEGRACIÓN DE PROVEEDOR IA PROPIO (LLM)</Text>
             
             <View style={[styles.card, { backgroundColor: colors.card, borderColor: colors.border }]}>
               <Text style={[styles.cardDescription, { color: colors.textSecondary, marginBottom: 12 }]}>
                 Para garantizar tu total privacidad y no depender de APIs centralizadas, puedes usar tu propio proveedor de inteligencia artificial. La app enviará las peticiones directamente a través de tu clave.
               </Text>
               
               <Text style={[styles.inputLabel, { color: colors.text, marginBottom: 8 }]}>Proveedor de IA</Text>
               <View style={{ flexDirection: 'row', gap: 8, marginBottom: 15 }}>
                 {(['GEMINI', 'OPENAI', 'ANTHROPIC'] as const).map((prov) => (
                   <TouchableOpacity
                     key={prov}
                     style={{
                       flex: 1,
                       height: 40,
                       borderRadius: 10,
                       backgroundColor: aiProvider === prov ? colors.primary : (isDark ? 'rgba(0,0,0,0.3)' : '#F0F4F8'),
                       justifyContent: 'center',
                       alignItems: 'center',
                       borderWidth: 1,
                       borderColor: aiProvider === prov ? colors.primary : colors.border
                     }}
                     onPress={() => setAiProvider(prov)}
                   >
                     <Text style={{ color: aiProvider === prov ? '#FFF' : colors.text, fontSize: 12, fontWeight: 'bold' }}>
                       {prov}
                     </Text>
                   </TouchableOpacity>
                 ))}
               </View>

               <Text style={[styles.inputLabel, { color: colors.text }]}>API Key Privada ({aiProvider})</Text>
               <TextInput
                 value={aiApiKey}
                 onChangeText={setAiApiKey}
                 secureTextEntry
                 placeholder={`Ingresa tu API Key de ${aiProvider}`}
                 placeholderTextColor={colors.textSecondary}
                 style={[styles.input, { color: colors.text, borderColor: colors.border, backgroundColor: isDark ? 'rgba(0,0,0,0.2)' : '#F9F9FB' }]}
               />
              </View>

              {/* AVISO DE PRIVACIDAD */}
              <View style={[styles.privacyBox, { backgroundColor: isDark ? 'rgba(76, 175, 80, 0.1)' : '#E8F5E9', borderColor: 'rgba(76, 175, 80, 0.2)' }]}>
                <Ionicons name="shield-checkmark" size={24} color="#4CAF50" style={{ marginRight: 12, marginTop: 2 }} />
                <View style={{ flex: 1 }}>
                  {/* El texto anterior decía "Compromiso de Privacidad Absoluta —
                      tus API Keys NUNCA pasan por nuestra plataforma... se
                      guardan localmente en este dispositivo". Era falso y se
                      desmentía en este mismo archivo: las claves se envían a
                      nuestro servidor. Declarar eso mal en Data Safety es motivo
                      de retirada de Google Play, y engañar al usuario sobre
                      dónde viven sus claves no se hace. Ahora dice la verdad. */}
                  <Text style={[styles.privacyTitle, { color: isDark ? '#81C784' : '#2E7D32' }]}>Cómo guardamos tus claves</Text>
                  <Text style={[styles.privacyText, { color: isDark ? '#A5D6A7' : '#388E3C' }]}>
                    Tus claves se envían por conexión segura y se guardan cifradas en nuestro
                    servidor. Solo se usan para que tu asistente responda por ti; nadie de Go Safely
                    las consulta ni se comparten con terceros. Puedes borrarlas cuando quieras desde
                    esta misma pantalla.
                  </Text>
                </View>
              </View>

             <Text style={[styles.sectionTitle, { color: colors.textSecondary }]}>INTEGRACIÓN WHATSAPP BUSINESS API</Text>
            
            <View style={[styles.card, { backgroundColor: colors.card, borderColor: colors.border }]}>
              <Text style={[styles.inputLabel, { color: colors.text }]}>Número de Teléfono del Bot</Text>
              <TextInput
                value={whatsappPhone}
                onChangeText={setWhatsappPhone}
                placeholder="Escribe el teléfono con código de país"
                placeholderTextColor={colors.textSecondary}
                style={[styles.input, { color: colors.text, borderColor: colors.border, backgroundColor: isDark ? 'rgba(0,0,0,0.2)' : '#F9F9FB', marginBottom: 15 }]}
              />

              <Text style={[styles.inputLabel, { color: colors.text }]}>WhatsApp API Key / Token</Text>
              <TextInput
                value={whatsappKey}
                onChangeText={setWhatsappKey}
                secureTextEntry
                placeholder="Token de Acceso de WhatsApp"
                placeholderTextColor={colors.textSecondary}
                style={[styles.input, { color: colors.text, borderColor: colors.border, backgroundColor: isDark ? 'rgba(0,0,0,0.2)' : '#F9F9FB', marginBottom: 15 }]}
              />

              <Text style={[styles.inputLabel, { color: colors.text }]}>ID del Número (Phone Number ID de Meta)</Text>
              <TextInput
                value={whatsappPhoneNumberId}
                onChangeText={setWhatsappPhoneNumberId}
                placeholder="Ej. 123456789012345"
                keyboardType="number-pad"
                placeholderTextColor={colors.textSecondary}
                style={[styles.input, { color: colors.text, borderColor: colors.border, backgroundColor: isDark ? 'rgba(0,0,0,0.2)' : '#F9F9FB', marginBottom: 15 }]}
              />

              <Text style={[styles.inputLabel, { color: colors.text }]}>Token de Verificación (lo eliges tú)</Text>
              <TextInput
                value={whatsappVerifyToken}
                onChangeText={setWhatsappVerifyToken}
                placeholder="Una palabra secreta que pondrás igual en Meta"
                placeholderTextColor={colors.textSecondary}
                style={[styles.input, { color: colors.text, borderColor: colors.border, backgroundColor: isDark ? 'rgba(0,0,0,0.2)' : '#F9F9FB', marginBottom: 15 }]}
              />

              <Text style={[styles.inputLabel, { color: colors.text }]}>URL del Webhook (pégala en tu app de Meta)</Text>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <Text numberOfLines={1} style={{ flex: 1, color: colors.textSecondary, fontSize: 12 }}>{webhookUrl}</Text>
                <TouchableOpacity onPress={async () => { await Clipboard.setStringAsync(webhookUrl); if (Platform.OS === 'web') window.alert('URL copiada'); else Alert.alert('Copiado', 'URL del webhook copiada.'); }}>
                  <Ionicons name="copy-outline" size={20} color={colors.primary} />
                </TouchableOpacity>
              </View>
            </View>

            <Text style={[styles.sectionTitle, { color: colors.textSecondary }]}>ROL E INSTRUCCIONES DEL BOT DE WHATSAPP</Text>
            <View style={[styles.card, { backgroundColor: colors.card, borderColor: colors.border }]}>
              <Text style={[styles.inputLabel, { color: colors.text }]}>Instrucciones de Comportamiento / Prompt</Text>
              <TextInput
                value={botPrompt}
                onChangeText={setBotPrompt}
                multiline
                numberOfLines={4}
                placeholder="Ej: Eres un asistente virtual inteligente..."
                placeholderTextColor={colors.textSecondary}
                style={[styles.input, { color: colors.text, borderColor: colors.border, backgroundColor: isDark ? 'rgba(0,0,0,0.2)' : '#F9F9FB', height: 80, textAlignVertical: 'top', paddingTop: 8 }]}
              />
            </View>

            <Text style={[styles.sectionTitle, { color: colors.textSecondary }]}>AUTOMATIZACIONES ACTIVAS</Text>
            <View style={[styles.card, { backgroundColor: colors.card, borderColor: colors.border }]}>
              <View style={styles.rowBetween}>
                <View style={{ flex: 1, paddingRight: 10 }}>
                  <Text style={[styles.settingTitle, { color: colors.text }]}>Alertas de Viajes</Text>
                  <Text style={[styles.settingDesc, { color: colors.textSecondary }]}>Notifica al dueño por WhatsApp cuando un chofer inicie o termine un viaje.</Text>
                </View>
                <Switch value={tripAlerts} onValueChange={setTripAlerts} trackColor={{ false: "#767577", true: colors.primary }} />
              </View>

              <View style={[styles.divider, { backgroundColor: colors.border, marginVertical: 15 }]} />

              <View style={styles.rowBetween}>
                <View style={{ flex: 1, paddingRight: 10 }}>
                  <Text style={[styles.settingTitle, { color: colors.text }]}>Recordatorios de Pago de Renta</Text>
                  <Text style={[styles.settingDesc, { color: colors.textSecondary }]}>Envía mensajes automáticos semanales al chofer cobrando la renta del vehículo.</Text>
                </View>
                <Switch value={paymentAlerts} onValueChange={setPaymentAlerts} trackColor={{ false: "#767577", true: colors.primary }} />
              </View>
            </View>

            <TouchableOpacity 
              style={[styles.saveBtn, { backgroundColor: colors.primary }]}
              onPress={handleSaveConfig}
              disabled={isSaving}
            >
              {isSaving ? (
                <ActivityIndicator size="small" color="#FFF" />
              ) : (
                <Text style={styles.saveBtnText}>Guardar Todo y Aplicar</Text>
              )}
            </TouchableOpacity>
          </View>
        )}

        <View style={{ height: 100 }} />
      </ScrollView>

      {/* Modal Asignación Vehículo */}
      {candidateToAssign && (
        <View style={[StyleSheet.absoluteFill, { backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center', zIndex: 999 }]}>
          <View style={{ width: '90%', maxWidth: 400, backgroundColor: colors.card, borderRadius: 20, padding: 20 }}>
            <Text style={{ fontSize: 18, fontWeight: 'bold', color: colors.text, marginBottom: 10 }}>Asignar Vehículo a {candidateToAssign.name}</Text>
            <Text style={{ color: colors.textSecondary, marginBottom: 15 }}>
              Elige el vehículo que le vas a entregar. Al aprobarlo, ese carro queda marcado como
              entregado y deja de salir publicado, y a él le llega el aviso.
            </Text>
            
            <ScrollView style={{ maxHeight: 200, marginBottom: 15 }}>
              {vehicles.filter(v => v.status === 'Disponible').map(v => (
                <TouchableOpacity 
                  key={v.id} 
                  style={{ padding: 12, borderBottomWidth: 1, borderColor: colors.border }}
                  onPress={() => handleApproveCandidate(v.id)}
                >
                  {/* Decía `v.brand` y `v.plate`, dos campos que no existen: el
                      vehículo se guarda con `make` (fleet.tsx) y no se le pide
                      placa en ninguna parte. Salía la marca en blanco y un
                      " - 2020" suelto. */}
                  {/* Decía `v.brand` y `v.plate`, dos campos que solo existen
                      en los carros dados de alta por el formulario del panel.
                      `leerVehiculo` lee las dos formas. */}
                  <Text style={{ color: colors.text, fontWeight: 'bold' }}>{leerVehiculo(v).titulo}</Text>
                  <Text style={{ color: colors.textSecondary, fontSize: 12 }}>
                    {leerVehiculo(v).year ? `${leerVehiculo(v).year} · ` : ''}{pesos(v.price)}/sem
                    {leerVehiculo(v).plate ? ` · ${leerVehiculo(v).plate}` : ''}
                  </Text>
                </TouchableOpacity>
              ))}
              {vehicles.filter(v => v.status === 'Disponible').length === 0 && (
                <Text style={{ color: colors.textSecondary, textAlign: 'center', padding: 10 }}>No tienes vehículos disponibles en este momento.</Text>
              )}
            </ScrollView>

            <View style={{ flexDirection: 'row', justifyContent: 'flex-end', gap: 10 }}>
              <TouchableOpacity onPress={() => setCandidateToAssign(null)} style={{ padding: 10 }}>
                <Text style={{ color: '#F44336', fontWeight: 'bold' }}>Cancelar</Text>
              </TouchableOpacity>
              {/* ⚠️ AQUÍ HABÍA UN «Aprobar sin Vehículo». No se puede: sin
                  carro elegido, el candidato no sabe cuál recoger, el carro no
                  deja de estar publicado y la ficha se queda en «Pendiente»
                  para siempre. Era el botón que convertía la aprobación en
                  nada. Para aprobar se toca el carro de la lista de arriba. */}
            </View>
          </View>
        </View>
      )}

    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 15,
    borderBottomWidth: 1,
  },
  backButton: {
    padding: 8,
    marginLeft: -8,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
  },
  tabBar: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    borderBottomWidth: 1,
  },
  tabButton: {
    paddingVertical: 12,
    paddingHorizontal: 10,
    borderBottomWidth: 3,
    borderBottomColor: 'transparent',
  },
  tabText: {
    fontSize: 14,
    fontWeight: 'bold',
  },
  container: {
    padding: 20,
  },
  card: {
    borderRadius: 20,
    padding: 20,
    marginBottom: 16,
    borderWidth: 1,
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 6,
  },
  cardDescription: {
    fontSize: 13,
    lineHeight: 18,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 'bold',
    marginBottom: 10,
    marginLeft: 4,
    letterSpacing: 1,
  },
  rowBetween: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  kpiContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 12,
  },
  kpiCard: {
    flex: 1,
    borderRadius: 16,
    padding: 16,
    borderWidth: 1,
    alignItems: 'flex-start',
  },
  kpiValue: {
    fontSize: 18,
    fontWeight: 'bold',
    marginVertical: 4,
  },
  kpiLabel: {
    fontSize: 11,
  },
  driverName: {
    fontSize: 15,
    fontWeight: 'bold',
    marginBottom: 2,
  },
  statusBadge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 8,
  },
  statusText: {
    fontSize: 12,
    fontWeight: 'bold',
  },
  divider: {
    height: 1,
  },
  copyBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    height: 48,
    borderRadius: 12,
    borderWidth: 1,
  },
  copyBtnText: {
    fontSize: 14,
    fontWeight: 'bold',
    marginLeft: 10,
  },
  docBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  actionBtn: {
    height: 42,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 15,
  },
  actionBtnText: {
    color: '#FFF',
    fontSize: 13,
    fontWeight: 'bold',
  },
  inputLabel: {
    fontSize: 13,
    fontWeight: '600',
    marginBottom: 6,
  },
  input: {
    height: 46,
    borderRadius: 10,
    borderWidth: 1,
    paddingHorizontal: 15,
    fontSize: 14,
  },
  settingTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 2,
  },
  settingDesc: {
    fontSize: 12,
    lineHeight: 16,
  },
  saveBtn: {
    height: 48,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 15,
  },
  saveBtnText: {
    color: '#FFF',
    fontSize: 15,
    fontWeight: 'bold',
  },
  privacyBox: {
    flexDirection: 'row',
    borderRadius: 16,
    padding: 16,
    borderWidth: 1,
    marginBottom: 20,
    alignItems: 'flex-start',
  },
  privacyTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  privacyText: {
    fontSize: 12,
    lineHeight: 18,
  }
});
